import { useState, useCallback, useRef } from 'react'
import { Link } from 'react-router-dom'
import {
  ArrowDown,
  ArrowRight,
  ArrowUp,
  Check,
  Users,
  Zap,
  Shield,
  GripVertical,
  AlertCircle,
  Loader2,
} from 'lucide-react'
import { Turnstile, type TurnstileInstance } from '@marsidev/react-turnstile'
import { PageMeta } from '@/components/seo/PageMeta'
import { LandingLayout } from '@/components/layout/LandingLayout'
import { StickySplit } from '@/components/landing/Editorial'
import { FormField, formLinkClass } from '@/components/landing/EditorialForm'
import { OfficeAppIcon, type OfficeApp } from '@/components/landing/OfficeAppIcon'
import { useToast } from '@/components/ui/use-toast'
import {
  BandReveal,
  HeroReveal,
  InteractiveDots,
  Pressable,
  ScrollReveal,
  StaggerContainer,
  StaggerItem,
  TextReveal,
} from '@/components/motion'
import { cn } from '@/lib/utils'
import { betaService, type TeamSize, type SubmitBetaRegistrationRequest } from '@/lib/api/services/beta'

// Cloudflare Turnstile site key - use test key for development
const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY || '1x00000000000000000000AA'

const teamSizeOptions: { value: TeamSize; label: string }[] = [
  { value: 'Solo', label: 'Just me' },
  { value: 'Small', label: '2-10 people' },
  { value: 'Medium', label: '11-50 people' },
  { value: 'Large', label: '51-200 people' },
  { value: 'Enterprise', label: '201-500 people' },
  { value: 'VeryLargeEnterprise', label: '500+ people' },
]

const officeApps: { id: string; app: OfficeApp; label: string }[] = [
  { id: 'Word', app: 'word', label: 'Word' },
  { id: 'Excel', app: 'excel', label: 'Excel' },
  { id: 'PowerPoint', app: 'powerpoint', label: 'PowerPoint' },
  { id: 'Outlook', app: 'outlook', label: 'Outlook' },
]

const industryOptions = [
  // Platform industries
  { value: 'Legal', label: 'Legal' },
  { value: 'Finance', label: 'Finance & Banking' },
  { value: 'Healthcare', label: 'Healthcare' },
  { value: 'Insurance', label: 'Insurance' },
  { value: 'Engineering', label: 'Engineering' },
  { value: 'Consulting', label: 'Consulting' },
  // Additional common industries
  { value: 'Technology', label: 'Technology & Software' },
  { value: 'Manufacturing', label: 'Manufacturing' },
  { value: 'Real Estate', label: 'Real Estate' },
  { value: 'Retail', label: 'Retail & E-commerce' },
  { value: 'Education', label: 'Education' },
  { value: 'Government', label: 'Government & Public Sector' },
  { value: 'Nonprofit', label: 'Nonprofit & NGO' },
  { value: 'Media', label: 'Media & Entertainment' },
  { value: 'Telecommunications', label: 'Telecommunications' },
  { value: 'Energy', label: 'Energy & Utilities' },
  { value: 'Transportation', label: 'Transportation & Logistics' },
  { value: 'Hospitality', label: 'Hospitality & Tourism' },
  { value: 'Agriculture', label: 'Agriculture' },
  { value: 'Pharmaceutical', label: 'Pharmaceutical & Biotech' },
]

const benefits = [
  {
    icon: Zap,
    title: 'Early Access',
    description: 'Be the first to try new features before public release',
  },
  {
    icon: Users,
    title: 'Shape the Product',
    description: 'Your feedback directly influences our roadmap',
  },
  {
    icon: Shield,
    title: 'Priority Support',
    description: 'Get dedicated support from our founding team',
  },
]

const challengeOptions = [
  { id: 'time', label: 'Too much time spent on repetitive formatting' },
  { id: 'consistency', label: 'Maintaining brand consistency across documents' },
  { id: 'templates', label: 'Finding and using the right templates' },
  { id: 'collaboration', label: 'Collaborating with team on documents' },
  { id: 'version', label: 'Version control and tracking changes' },
  { id: 'data', label: 'Pulling data from multiple sources' },
  { id: 'compliance', label: 'Ensuring compliance and legal requirements' },
  { id: 'automation', label: 'Lack of automation for document workflows' },
]

// Well-known personal email domains (allowed, but show preference for work email)
const personalEmailDomains = [
  'gmail.com', 'googlemail.com', 'yahoo.com', 'yahoo.co.uk', 'yahoo.fr', 'yahoo.de',
  'hotmail.com', 'hotmail.co.uk', 'outlook.com', 'live.com', 'msn.com',
  'aol.com', 'icloud.com', 'me.com', 'mac.com',
  'mail.com', 'email.com', 'protonmail.com', 'proton.me', 'tutanota.com',
  'zoho.com', 'yandex.com', 'yandex.ru', 'gmx.com', 'gmx.de',
  'fastmail.com', 'hey.com', 'pm.me',
]

// Disposable/temporary email domains (blocked)
const disposableEmailDomains = [
  'tempmail.com', 'temp-mail.org', 'guerrillamail.com', 'guerrillamail.org',
  'mailinator.com', 'maildrop.cc', '10minutemail.com', '10minutemail.net',
  'throwaway.email', 'fakeinbox.com', 'sharklasers.com', 'mailnesia.com',
  'tempr.email', 'discard.email', 'yopmail.com', 'mohmal.com',
  'getnada.com', 'getairmail.com', 'trashmail.com', 'trashmail.net',
  'mailcatch.com', 'spamgourmet.com', 'mintemail.com', 'tempinbox.com',
  'emailondeck.com', 'fakemailgenerator.com', 'throwawaymail.com',
]

export default function JoinBeta() {
  const [submitted, setSubmitted] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [appPriorities, setAppPriorities] = useState<string[]>(['Word', 'Excel', 'PowerPoint', 'Outlook'])
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null)
  const [selectedChallenges, setSelectedChallenges] = useState<string[]>([])
  const [emailWarning, setEmailWarning] = useState<string | null>(null)
  const [emailError, setEmailError] = useState<string | null>(null)
  const [captchaToken, setCaptchaToken] = useState<string | null>(null)
  const [formError, setFormError] = useState<string | null>(null)
  const turnstileRef = useRef<TurnstileInstance>(null)
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    company: '',
    jobTitle: '',
    industry: '',
    teamSize: '' as TeamSize | '',
    challengesOther: '',
    expectations: '',
  })
  const { toast } = useToast()

  const validateEmail = useCallback((email: string) => {
    const domain = email.split('@')[1]?.toLowerCase()
    setFormError(null)

    if (!domain) {
      setEmailError(null)
      setEmailWarning(null)
      return
    }

    // Check for disposable email domains (blocked)
    if (disposableEmailDomains.some(d => domain === d || domain.endsWith('.' + d))) {
      setEmailError('Temporary or disposable email addresses are not allowed')
      setEmailWarning(null)
      return
    }

    // Check for suspicious domain patterns (gibberish detection)
    const suspiciousPatterns = [
      /^[a-z]{20,}\.(com|net|org)$/,  // Very long random-looking domains
      /^\d+[a-z]+\d+\./,               // Numbers mixed with letters suspiciously
      /^[bcdfghjklmnpqrstvwxz]{5,}\./,  // Too many consonants (likely gibberish)
      /temp|trash|fake|spam|disposable|throwaway/i,  // Keywords
    ]

    if (suspiciousPatterns.some(pattern => pattern.test(domain))) {
      setEmailError('Please use a valid email address from a recognized provider')
      setEmailWarning(null)
      return
    }

    setEmailError(null)

    // Show preference for work email if using personal domain
    if (personalEmailDomains.includes(domain)) {
      setEmailWarning('Work email preferred for faster approval, but personal email is accepted')
    } else {
      setEmailWarning(null)
    }
  }, [])

  const toggleChallenge = useCallback((challengeId: string) => {
    setSelectedChallenges(prev =>
      prev.includes(challengeId)
        ? prev.filter(id => id !== challengeId)
        : [...prev, challengeId]
    )
  }, [])

  const handleDragStart = useCallback((index: number) => {
    setDraggedIndex(index)
  }, [])

  const handleDragOver = useCallback((e: React.DragEvent, index: number) => {
    e.preventDefault()
    if (draggedIndex === null || draggedIndex === index) return

    const newPriorities = [...appPriorities]
    const [removed] = newPriorities.splice(draggedIndex, 1)
    if (removed) newPriorities.splice(index, 0, removed)
    setAppPriorities(newPriorities)
    setDraggedIndex(index)
  }, [draggedIndex, appPriorities])

  const handleDragEnd = useCallback(() => {
    setDraggedIndex(null)
  }, [])

  // Keyboard and touch alternative to dragging: move one step up or down.
  const moveApp = useCallback((index: number, delta: -1 | 1) => {
    setAppPriorities((prev) => {
      const target = index + delta
      if (target < 0 || target >= prev.length) return prev
      const next = [...prev]
      ;[next[index], next[target]] = [next[target]!, next[index]!]
      return next
    })
  }, [])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setFormError(null)

    // Validate email first
    if (emailError) {
      setFormError(emailError)
      return
    }

    // Check CAPTCHA token
    if (!captchaToken) {
      setFormError('Please complete the security verification')
      return
    }

    setSubmitting(true)

    try {
      // Combine selected challenges with custom input
      const challengeLabels = selectedChallenges
        .map(id => challengeOptions.find(c => c.id === id)?.label)
        .filter(Boolean)
      if (formData.challengesOther) {
        challengeLabels.push(formData.challengesOther)
      }
      const challengesText = challengeLabels.join('; ')

      const request: SubmitBetaRegistrationRequest = {
        fullName: formData.fullName,
        email: formData.email,
        company: formData.company,
        jobTitle: formData.jobTitle,
        industry: formData.industry || undefined,
        teamSize: formData.teamSize || undefined,
        officeAppPriorities: appPriorities,
        challenges: challengesText || undefined,
        expectations: formData.expectations || undefined,
        referralSource: 'website',
      }

      await betaService.submitRegistration(request)
      setSubmitted(true)
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Please try again later.'
      setFormError(errorMessage)
      toast({
        title: 'Registration Failed',
        description: errorMessage,
        variant: 'destructive',
      })
      // Reset CAPTCHA on error so user can try again
      turnstileRef.current?.reset()
      setCaptchaToken(null)
    } finally {
      setSubmitting(false)
    }
  }

  const sectionTitle =
    'mb-5 font-[family-name:var(--font-landing-subdisplay)] text-xl font-semibold tracking-[-0.01em] text-[var(--color-landing-gray-900)] dark:text-white'
  const hint = 'mt-1.5 text-xs text-[var(--color-landing-gray-600)] dark:text-[var(--color-landing-gray-400)]'

  return (
    <>
      <PageMeta
        title="Join the Beta | Templatean"
        description="Be among the first to experience Templatean. Join our beta program and help shape the future of document automation."
      />

      <LandingLayout>
        {/* Hero: highlighted statement, quote-bar lede and a jump to the form */}
        <section className="landing-container pt-16 pb-20 md:pt-24 lg:pb-28">
          <TextReveal
            as="h1"
            lines={['Be an early ', <span key="hl" className="inline-block -rotate-1 bg-[#ff00f2] px-[0.25em] text-white">pioneer.</span>]}
            className="landing-text-hero text-[var(--color-landing-gray-900)] dark:text-white"
          />
          <HeroReveal delay={0.25}>
            <div className="mt-12 flex flex-col gap-10 lg:mt-16 lg:flex-row lg:items-end lg:justify-between lg:gap-24">
              <p className="max-w-[56ch] border-l border-[var(--color-landing-gray-300)] pl-5 landing-text-body-lg text-[var(--color-landing-gray-600)] dark:border-[var(--color-landing-gray-700)] dark:text-[var(--color-landing-gray-400)]">
                Join our beta program and help shape the future of AI-powered document automation.
                Get exclusive early access and direct influence on our product roadmap.
              </p>
              <Pressable className="self-start lg:self-end">
                <a href="#request-access" className="landing-cta landing-cta--solid landing-cta--lg landing-btn-sweep">
                  Request access
                  <ArrowDown className="h-5 w-5" />
                </a>
              </Pressable>
            </div>
          </HeroReveal>
        </section>

        {/* Benefits on a blue halftone band, hairline columns */}
        <BandReveal>
          <section className="relative overflow-hidden bg-[var(--color-landing-accent)] text-white">
            <InteractiveDots />
            <div className="landing-container landing-section-sm relative z-10">
              <h2 className="sr-only">Beta benefits</h2>
              <StaggerContainer className="grid gap-10 md:grid-cols-3 md:gap-0 md:divide-x md:divide-white/25">
                {benefits.map((benefit) => (
                  <StaggerItem key={benefit.title} className="md:px-10 md:first:pl-0 md:last:pr-0">
                    <span className="mb-8 flex h-12 w-12 items-center justify-center bg-white text-[var(--color-landing-accent)]">
                      <benefit.icon className="h-6 w-6" strokeWidth={1.75} aria-hidden />
                    </span>
                    <h3 className="font-[family-name:var(--font-landing-display)] text-[1.625rem] uppercase leading-[1.3] tracking-[-0.02em]">
                      {benefit.title}
                    </h3>
                    <p className="mt-3 max-w-[32ch] landing-text-body text-white/85">{benefit.description}</p>
                  </StaggerItem>
                ))}
              </StaggerContainer>
            </div>
          </section>
        </BandReveal>

        {/* Registration: sticky header left, the form right */}
        <section id="request-access" className="landing-section scroll-mt-20">
          <div className="landing-container">
            <StickySplit
              side="left"
              headerWidth="narrow"
              header={
                <>
                  <TextReveal as="h2" className="landing-text-display-xl mb-10 text-[var(--color-landing-gray-900)] dark:text-white">
                    {submitted ? (
                      <>You're on the{' '}<span className="landing-highlight">list!</span></>
                    ) : (
                      <>Request beta{' '}<span className="landing-highlight">access.</span></>
                    )}
                  </TextReveal>
                  <ScrollReveal direction="none" delay={0.15}>
                    <p className="max-w-md border-l border-[var(--color-landing-gray-300)] pl-5 landing-text-body-sm text-[var(--color-landing-gray-600)] dark:border-[var(--color-landing-gray-700)] dark:text-[var(--color-landing-gray-300)]">
                      {submitted
                        ? "Check your inbox for a verification email. Click the link to confirm your spot and we'll notify you when we're ready to welcome you aboard."
                        : "Tell us about yourself and what you're looking to achieve with Templatean."}
                    </p>
                  </ScrollReveal>
                </>
              }
            >
              {submitted ? (
                <div className="border-t border-[var(--color-landing-gray-900)] pt-10 dark:border-white/40">
                  <span className="flex h-16 w-16 items-center justify-center bg-[#00d722] text-[var(--color-landing-primary)]">
                    <Check className="h-8 w-8" strokeWidth={2.5} aria-hidden />
                  </span>
                  <Link to="/" className="landing-cta landing-cta--outline landing-cta--lg landing-btn-sweep mt-10">
                    Return to Home
                  </Link>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-14">
                  {/* About you */}
                  <fieldset>
                    <legend className={sectionTitle}>About you</legend>
                    <div className="grid gap-3 sm:grid-cols-2">
                      <FormField id="fullName" label="Full name (required)">
                        <input
                          id="fullName"
                          value={formData.fullName}
                          onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                          placeholder="Full name *"
                          autoComplete="name"
                          required
                          className="landing-field"
                        />
                      </FormField>
                      <FormField id="email" label="Email (required)">
                        <input
                          id="email"
                          type="email"
                          value={formData.email}
                          onChange={(e) => {
                            setFormData({ ...formData, email: e.target.value })
                            validateEmail(e.target.value)
                          }}
                          placeholder="Work email *"
                          autoComplete="email"
                          required
                          aria-invalid={!!emailError}
                          aria-describedby="email-note"
                          className={cn(
                            'landing-field',
                            emailError && '!shadow-[inset_0_0_0_2px_#ee1d36]',
                            emailWarning && !emailError && 'shadow-[inset_0_0_0_2px_#f59e0b]'
                          )}
                        />
                        <div id="email-note" aria-live="polite">
                          {emailError && (
                            <p className="mt-1.5 flex items-center gap-1.5 text-xs text-[#b3001b] dark:text-[#ff8a99]">
                              <AlertCircle className="h-3.5 w-3.5 shrink-0" aria-hidden />
                              {emailError}
                            </p>
                          )}
                          {emailWarning && !emailError && <p className={hint}>{emailWarning}</p>}
                        </div>
                      </FormField>
                      <FormField id="company" label="Company (required)">
                        <input
                          id="company"
                          value={formData.company}
                          onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                          placeholder="Company *"
                          autoComplete="organization"
                          required
                          className="landing-field"
                        />
                      </FormField>
                      <FormField id="jobTitle" label="Job title (required)">
                        <input
                          id="jobTitle"
                          value={formData.jobTitle}
                          onChange={(e) => setFormData({ ...formData, jobTitle: e.target.value })}
                          placeholder="Job title *"
                          autoComplete="organization-title"
                          required
                          className="landing-field"
                        />
                      </FormField>
                      <FormField id="industry" label="Industry">
                        <input
                          id="industry"
                          list="industry-options"
                          value={formData.industry}
                          onChange={(e) => setFormData({ ...formData, industry: e.target.value })}
                          placeholder="Industry"
                          aria-describedby="industry-hint"
                          className="landing-field"
                        />
                        <datalist id="industry-options">
                          {industryOptions.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </datalist>
                        <p id="industry-hint" className={hint}>Select from list or type your own</p>
                      </FormField>
                      <FormField id="teamSize" label="Team size">
                        <select
                          id="teamSize"
                          value={formData.teamSize}
                          onChange={(e) => setFormData({ ...formData, teamSize: e.target.value as TeamSize })}
                          className="landing-field"
                        >
                          <option value="">Team size</option>
                          {teamSizeOptions.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </FormField>
                    </div>
                  </fieldset>

                  {/* Office App Priority Ranker */}
                  <fieldset>
                    <legend className={sectionTitle}>
                      Rank Office apps by priority
                      <span className="ml-2 text-sm font-normal text-[var(--color-landing-gray-600)] dark:text-[var(--color-landing-gray-400)]">
                        (drag or use the arrows)
                      </span>
                    </legend>
                    <ol className="border-t border-[var(--color-landing-gray-900)] dark:border-white/40">
                      {appPriorities.map((appId, index) => {
                        const app = officeApps.find((a) => a.id === appId)!
                        return (
                          <li
                            key={appId}
                            draggable
                            onDragStart={() => handleDragStart(index)}
                            onDragOver={(e) => handleDragOver(e, index)}
                            onDragEnd={handleDragEnd}
                            className={cn(
                              'flex cursor-grab items-center gap-4 border-b border-[var(--color-landing-gray-200)] py-3 transition-[opacity,background-color] duration-200 active:cursor-grabbing dark:border-[var(--color-landing-gray-800)] sm:gap-6',
                              draggedIndex === index ? 'bg-[var(--color-landing-gray-50)] opacity-60 dark:bg-white/[0.04]' : 'hover:bg-[var(--color-landing-gray-50)] dark:hover:bg-white/[0.03]'
                            )}
                          >
                            <GripVertical className="h-5 w-5 shrink-0 text-[var(--color-landing-gray-500)]" aria-hidden />
                            <span className="w-10 font-[family-name:var(--font-landing-display)] text-2xl leading-none text-[var(--color-landing-accent)] dark:text-[#8b85ff]">
                              {index + 1}
                            </span>
                            <OfficeAppIcon app={app.app} className="h-9 w-9 shrink-0" />
                            <span className="flex-1 font-semibold text-[var(--color-landing-gray-900)] dark:text-white">{app.label}</span>
                            <span className="flex gap-1">
                              <button
                                type="button"
                                onClick={() => moveApp(index, -1)}
                                disabled={index === 0}
                                aria-label={`Move ${app.label} up`}
                                className="flex h-9 w-9 items-center justify-center border border-[var(--color-landing-gray-300)] text-[var(--color-landing-gray-700)] transition-colors hover:border-[var(--color-landing-accent)] hover:text-[var(--color-landing-accent)] disabled:opacity-30 disabled:hover:border-[var(--color-landing-gray-300)] disabled:hover:text-[var(--color-landing-gray-700)] dark:border-[var(--color-landing-gray-700)] dark:text-[var(--color-landing-gray-300)]"
                              >
                                <ArrowUp className="h-4 w-4" aria-hidden />
                              </button>
                              <button
                                type="button"
                                onClick={() => moveApp(index, 1)}
                                disabled={index === appPriorities.length - 1}
                                aria-label={`Move ${app.label} down`}
                                className="flex h-9 w-9 items-center justify-center border border-[var(--color-landing-gray-300)] text-[var(--color-landing-gray-700)] transition-colors hover:border-[var(--color-landing-accent)] hover:text-[var(--color-landing-accent)] disabled:opacity-30 disabled:hover:border-[var(--color-landing-gray-300)] disabled:hover:text-[var(--color-landing-gray-700)] dark:border-[var(--color-landing-gray-700)] dark:text-[var(--color-landing-gray-300)]"
                              >
                                <ArrowDown className="h-4 w-4" aria-hidden />
                              </button>
                            </span>
                          </li>
                        )
                      })}
                    </ol>
                  </fieldset>

                  {/* Challenges */}
                  <fieldset>
                    <legend className={sectionTitle}>
                      What challenges do you face with document creation today?
                      <span className="ml-2 text-sm font-normal text-[var(--color-landing-gray-600)] dark:text-[var(--color-landing-gray-400)]">
                        (select all that apply)
                      </span>
                    </legend>
                    <div className="grid gap-2 sm:grid-cols-2">
                      {challengeOptions.map((challenge) => {
                        const selected = selectedChallenges.includes(challenge.id)
                        return (
                          <button
                            key={challenge.id}
                            type="button"
                            aria-pressed={selected}
                            onClick={() => toggleChallenge(challenge.id)}
                            className={cn(
                              'flex items-start gap-3 border px-4 py-3.5 text-left text-[15px] leading-snug transition-colors duration-200',
                              selected
                                ? 'border-[var(--color-landing-accent)] bg-[var(--color-landing-accent)]/[0.06] text-[var(--color-landing-gray-900)] dark:bg-[var(--color-landing-accent)]/20 dark:text-white'
                                : 'border-[var(--color-landing-gray-300)] text-[var(--color-landing-gray-700)] hover:border-[var(--color-landing-gray-900)] dark:border-[var(--color-landing-gray-700)] dark:text-[var(--color-landing-gray-300)] dark:hover:border-white/60'
                            )}
                          >
                            <span
                              aria-hidden
                              className={cn(
                                'mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center border transition-colors duration-200',
                                selected ? 'border-[var(--color-landing-accent)] bg-[var(--color-landing-accent)]' : 'border-[var(--color-landing-gray-500)]'
                              )}
                            >
                              {selected && <Check className="h-3 w-3 text-white" strokeWidth={3} />}
                            </span>
                            {challenge.label}
                          </button>
                        )
                      })}
                    </div>
                    <FormField id="challengesOther" label="Other challenges" className="mt-3">
                      <input
                        id="challengesOther"
                        value={formData.challengesOther}
                        onChange={(e) => setFormData({ ...formData, challengesOther: e.target.value })}
                        placeholder="Other challenges"
                        className="landing-field"
                      />
                    </FormField>
                  </fieldset>

                  {/* Expectations */}
                  <fieldset>
                    <legend className={sectionTitle}>What features would be most valuable to you?</legend>
                    <FormField id="expectations" label="What features would be most valuable to you?">
                      <textarea
                        id="expectations"
                        value={formData.expectations}
                        onChange={(e) => setFormData({ ...formData, expectations: e.target.value })}
                        placeholder="Describe what you hope to achieve with Templatean..."
                        rows={4}
                        className="landing-field normal-case leading-[1.5] font-[family-name:var(--font-landing-body)] placeholder:font-[family-name:var(--font-landing-display)]"
                      />
                    </FormField>
                  </fieldset>

                  <div className="space-y-6 border-t border-[var(--color-landing-gray-900)] pt-10 dark:border-white/40">
                    {/* CAPTCHA */}
                    <Turnstile
                      ref={turnstileRef}
                      siteKey={TURNSTILE_SITE_KEY}
                      onSuccess={(token) => {
                        setCaptchaToken(token)
                        setFormError(null)
                      }}
                      onError={() => {
                        setCaptchaToken(null)
                        setFormError('Security verification failed. Please try again.')
                      }}
                      onExpire={() => {
                        setCaptchaToken(null)
                      }}
                      options={{
                        theme: 'auto',
                        size: 'normal',
                      }}
                    />

                    {/* Form Error Alert */}
                    {formError && (
                      <div role="alert" className="flex items-start gap-3 border border-[#ee1d36] bg-[#ee1d36]/[0.06] px-4 py-3">
                        <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-[#b3001b] dark:text-[#ff8a99]" aria-hidden />
                        <div>
                          <p className="text-sm font-semibold text-[#b3001b] dark:text-[#ff8a99]">Unable to submit registration</p>
                          <p className="mt-0.5 text-sm text-[#b3001b] dark:text-[#ff8a99]">{formError}</p>
                        </div>
                      </div>
                    )}

                    <div className="flex flex-col gap-5 sm:flex-row sm:items-center sm:gap-8">
                      <button
                        type="submit"
                        disabled={submitting || !!emailError}
                        className="landing-cta landing-cta--solid landing-cta--lg landing-btn-sweep self-start disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        {submitting ? (
                          <>
                            <Loader2 className="h-5 w-5 animate-spin" />
                            Submitting
                          </>
                        ) : (
                          <>
                            Request beta access
                            <ArrowRight className="h-5 w-5" />
                          </>
                        )}
                      </button>
                      <p className="max-w-xs text-sm text-[var(--color-landing-gray-600)] dark:text-[var(--color-landing-gray-400)]">
                        By submitting, you agree to our{' '}
                        <Link to="/terms" className={formLinkClass}>
                          Terms of Service
                        </Link>{' '}
                        and{' '}
                        <Link to="/privacy" className={formLinkClass}>
                          Privacy Policy
                        </Link>
                      </p>
                    </div>
                  </div>
                </form>
              )}
            </StickySplit>
          </div>
        </section>
      </LandingLayout>
    </>
  )
}
